// src/lib/time.js — Shared time formatting utilities

export function fmtTime(secs) {
  const s = Math.max(0, Math.floor(secs))
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Restituisce la data locale come stringa YYYY-MM-DD.
 * Evita il bug UTC: new Date().toISOString() su Europe/Rome (UTC+2)
 * a mezzanotte locale dà ancora il giorno precedente.
 *
 * @param {Date} [d] — opzionale, default = now
 * @returns {string} es. "2026-06-09"
 */
export function toLocalISO(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Giorno ISO della settimana (1=lunedì … 7=domenica), locale. */
export function isoDayOfWeek(d = new Date()) {
  return ((d.getDay() + 6) % 7) + 1
}

/**
 * Lunedì (a mezzanotte locale) della settimana che contiene `d`.
 * `plan.created_at` NON è detto sia lunedì — va agganciato al lunedì
 * reale della sua settimana, stessa convenzione usata lato DB in
 * database/backfill_plan_days.sql (lì fatto con isodow su Postgres).
 * Se un piano nasce di mercoledì e non si fa questo snap, tutta la
 * griglia Lun→Dom slitta di due giorni.
 */
export function mondayOfWeek(d = new Date()) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  date.setDate(date.getDate() - (isoDayOfWeek(date) - 1))
  return date
}

/** Giorni interi tra due date locali, robusto a DST (confronta componenti Y/M/D in UTC). */
function daysBetween(a, b) {
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.round((utcB - utcA) / 86400000)
}

/**
 * Numero di settimana (1-based, clampato a `totalWeeks`) del piano oggi.
 * Aggancia `createdAt` al lunedì reale della sua settimana prima di contare
 * le settimane trascorse — altrimenti un piano nato a metà settimana sfasa
 * il conteggio nei giorni tra la sua creazione e il lunedì successivo.
 * Condiviso da WeekPlanCard e Dashboard.hooks per non duplicare (e
 * disallineare) la stessa logica in due posti.
 */
/**
 * La settimana `weekNumber` del piano è ancora aperta (finisce oggi o dopo)?
 *
 * Serve a distinguere le due cose che `plan_day.status = 'skipped'` mescola:
 * "non l'ho fatta nel giorno previsto" e "non si può più fare". Finché la
 * settimana non è chiusa la sessione si recupera un altro giorno — è il modo
 * in cui la gente si allena davvero. Diventa saltata per davvero il lunedì
 * dopo, quando è oggettivamente impossibile averla fatta.
 *
 * Non passa da `currentWeekNumber` di proposito: quello clampa a `totalWeeks`,
 * e a piano finito l'ultima settimana risulterebbe aperta per sempre.
 */
export function isWeekOpen(weekNumber, createdAt) {
  const monday = mondayOfWeek(new Date(createdAt))
  monday.setDate(monday.getDate() + (weekNumber - 1) * 7)
  const sunday = new Date(monday)
  sunday.setDate(sunday.getDate() + 6)
  sunday.setHours(23, 59, 59, 999)
  return new Date() <= sunday
}

export function currentWeekNumber(createdAt, totalWeeks) {
  const startMonday = mondayOfWeek(new Date(createdAt))
  const todayMonday = mondayOfWeek(new Date())
  const elapsed = Math.floor(daysBetween(startMonday, todayMonday) / 7)
  return Math.min(Math.max(elapsed + 1, 1), totalWeeks)
}
